/**
 * BreakdownTable — one row per timekeeper, six metric columns, two groups.
 *
 * The column groups exist because the percentages do NOT share a denominator:
 *
 *   "Share of captured"  Tracked / Exported / Deleted — mutually exclusive
 *                        statuses over everything Hourglass captured. These
 *                        three sum to 100%.
 *   "vs billing"         Utilization — exported Hourglass hours over the
 *                        attorney's billed hours in the practice-management
 *                        system. An external denominator entirely.
 *   "Quality"            Manual — hand-created over everything exported.
 *
 * Mixing those in one flat header would invite reading the row as a single
 * composition, which it is not. The grouped header does that work without a
 * legend.
 *
 * Every cell leads with the percentage (what you scan and sort by) and carries
 * the raw fraction underneath. Composition columns keep the fraction on hover —
 * their denominator is the same for all three, so repeating it six times per
 * row is noise. Utilization and Manual show it always, because their
 * denominators are the surprising ones.
 */

import { Fragment, useMemo, useState } from "react";
import {
	ArrowDown,
	ArrowUp,
	Check,
	ChevronsUpDown,
	Eye,
	EyeOff,
	X,
} from "lucide-react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/shared/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { formatAmount, formatRate } from "../aggregate";
import { BAND_CLASS, type Band } from "../targets";
import type { Measure } from "../types";

export interface BreakdownCell {
	/** The percentage, or null when it cannot be computed. */
	pct: number | null;
	/** Raw numerator/denominator in the active measure. */
	numerator: number | null;
	denominator: number | null;
	/** Threshold band driving the cell's colour chip. */
	band?: Band | null;
	/** Replaces the fraction when the value is absent for a known reason. */
	absentReason?: string;
}

export interface BreakdownColumn {
	key: string;
	label: string;
	/** Header group this column sits under. Adjacent equal values merge. */
	group: string;
	help: string;
	/** Show the raw fraction without hovering. */
	alwaysShowFraction?: boolean;
	/**
	 * Overrides the table-wide measure when formatting this column's fraction,
	 * for a column whose numbers are not in the toggled unit — utilization is
	 * always hours, because the billing side has no entry count.
	 */
	fractionMeasure?: Measure;
	/**
	 * Direction applied when this column is first selected. Defaults to
	 * descending, which is right for every column whose higher number is the
	 * better one — but not for a rank, where 1 is best and descending would
	 * open on the worst performer.
	 */
	initialDesc?: boolean;
	cell: (rowId: string) => BreakdownCell;
	/**
	 * Replaces the default percentage-chip body. The column still supplies
	 * `cell()` so sorting keeps working off a comparable number.
	 */
	renderCell?: (rowId: string) => React.ReactNode;
	/** Rendered next to the column label. */
	headerAccessory?: React.ReactNode;
	/**
	 * Rendered at the right edge of this column's GROUP header — e.g. a scale
	 * explainer that describes the group rather than the single column.
	 */
	groupAccessory?: React.ReactNode;
}

export interface BreakdownRow {
	id: string;
	name: string;
}

/**
 * Splits the table into two labelled sections.
 *
 * The column sort runs first and the split preserves its order inside each
 * section, so "utilization descending, onboarded first" means exactly that.
 */
export interface TablePartition {
	/** True for rows in the `on` section. */
	of: (rowId: string) => boolean;
	labels: { on: string; off: string };
	/** True puts the `on` section first. */
	desc: boolean;
	onToggle: () => void;
	/**
	 * Flips one row's state, on double-click of its mark. Mouse-only by
	 * deliberate choice — there is no keyboard path to this.
	 */
	onToggleRow?: (rowId: string) => void;
	/** Shown in the corner header, e.g. "5 / 12 Onboarded". */
	summary: string;
}

interface BreakdownTableProps {
	rows: BreakdownRow[];
	columns: BreakdownColumn[];
	measure: Measure;
	defaultSort: string;
	defaultDesc?: boolean;
	expandedId: string | null;
	onToggleExpand: (id: string) => void;
	renderDetail: (id: string) => React.ReactNode;
	/** Rows excluded from the firm totals. Still listed, visibly dimmed. */
	excludedIds: ReadonlySet<string>;
	onToggleExclude: (id: string) => void;
	/**
	 * Rendered in a narrow trailing column at the table's right edge. A real
	 * column rather than an absolutely-positioned overlay, so row borders and
	 * hover backgrounds run the full width of the table.
	 */
	trailingCell?: (rowId: string) => React.ReactNode;
	/** Full-width row rendered beneath its row when it returns non-null. */
	rowPanel?: (rowId: string) => React.ReactNode | null;
	/** Splits rows into two sections and marks each row with its state. */
	partition?: TablePartition;
}

export function BreakdownTable({
	rows,
	columns,
	measure,
	defaultSort,
	defaultDesc = true,
	expandedId,
	onToggleExpand,
	renderDetail,
	excludedIds,
	onToggleExclude,
	trailingCell,
	rowPanel,
	partition,
}: BreakdownTableProps) {
	const [sortKey, setSortKey] = useState(defaultSort);
	const [desc, setDesc] = useState(defaultDesc);

	const sorted = useMemo(() => {
		const col = columns.find((c) => c.key === sortKey);
		if (!col) return rows;

		return [...rows].sort((a, b) => {
			const av = col.cell(a.id).pct;
			const bv = col.cell(b.id).pct;

			// Nulls sink to the bottom in BOTH directions. "No billing data" is not
			// "worst performer", and sorting it as zero would say exactly that.
			if (av === null && bv === null) return a.name.localeCompare(b.name);
			if (av === null) return 1;
			if (bv === null) return -1;

			return desc ? bv - av : av - bv;
		});
	}, [rows, columns, sortKey, desc]);

	/**
	 * Sorted rows, split in two. `filter` preserves the sort, so the column
	 * ordering survives inside each section without a second comparator.
	 */
	const sections = useMemo(() => {
		if (!partition) return [{ label: null, rows: sorted }];
		const on = {
			label: partition.labels.on,
			rows: sorted.filter((r) => partition.of(r.id)),
		};
		const off = {
			label: partition.labels.off,
			rows: sorted.filter((r) => !partition.of(r.id)),
		};
		return partition.desc ? [on, off] : [off, on];
	}, [sorted, partition]);

	/** Rows in display order, sections concatenated. */
	const ordered = useMemo(
		() => sections.flatMap((s) => s.rows),
		[sections],
	);

	/**
	 * Heading to draw above a given row — keyed on the first row of each
	 * non-empty section, so an empty section contributes nothing rather than a
	 * bare heading that reads as a rendering fault.
	 */
	const headingBefore = useMemo(() => {
		const map = new Map<string, string>();
		for (const s of sections) {
			if (s.label && s.rows.length > 0) map.set(s.rows[0].id, s.label);
		}
		return map;
	}, [sections]);

	function partitionLabel(rowId: string): string | undefined {
		if (!partition) return undefined;
		return partition.of(rowId) ? partition.labels.on : partition.labels.off;
	}

	function toggleSort(key: string) {
		if (key === sortKey) {
			setDesc((d) => !d);
		} else {
			setSortKey(key);
			// The column decides which end it opens on. Assuming descending here
			// put the worst satisfaction rating at the top of a column documented
			// as sorting best-first.
			setDesc(columns.find((c) => c.key === key)?.initialDesc ?? true);
		}
	}

	// Merge adjacent columns sharing a group into one spanning header cell. The
	// first column in a group that supplies a `groupAccessory` owns it.
	const groups = useMemo(() => {
		const out: Array<{
			label: string;
			span: number;
			accessory?: React.ReactNode;
		}> = [];
		for (const col of columns) {
			const last = out[out.length - 1];
			if (last && last.label === col.group) {
				last.span += 1;
				last.accessory = last.accessory ?? col.groupAccessory;
			} else {
				out.push({
					label: col.group,
					span: 1,
					accessory: col.groupAccessory,
				});
			}
		}
		return out;
	}, [columns]);

	return (
		<div className="overflow-x-auto rounded-lg border">
			<Table>
				<TableHeader>
					<TableRow className="hover:bg-transparent">
						<TableHead className="border-b-0 pb-0">
							{partition && (
								<button
									type="button"
									onClick={partition.onToggle}
									aria-label={`${partition.summary}. Click to put ${
										partition.desc ? partition.labels.off : partition.labels.on
									} first.`}
									className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
								>
									{partition.summary}
									{partition.desc ? (
										<ArrowDown className="size-3" />
									) : (
										<ArrowUp className="size-3" />
									)}
								</button>
							)}
						</TableHead>
						{groups.map((g, i) => (
							<TableHead
								key={`${g.label}-${i}`}
								colSpan={g.span}
								className={cn(
									"border-b-0 pb-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground",
									i > 0 && "border-l",
								)}
							>
								{/* Label stays centred in the full span; the accessory is
								    pulled out of flow so it cannot shift it off-centre. */}
								<span className="relative block text-center">
									{g.label}
									{g.accessory && (
										<span className="absolute top-0 right-0 leading-none">
											{g.accessory}
										</span>
									)}
								</span>
							</TableHead>
						))}
						{trailingCell && (
							<TableHead className="w-10 border-b-0 bg-muted/50" />
						)}
					</TableRow>
					<TableRow>
						<TableHead className="text-xs">Timekeeper</TableHead>
						{columns.map((col, i) => {
							const active = col.key === sortKey;
							const Icon = active ? (desc ? ArrowDown : ArrowUp) : ChevronsUpDown;
							const startsGroup =
								i > 0 && columns[i - 1].group !== col.group;
							return (
								<TableHead
									key={col.key}
									className={cn("text-right", startsGroup && "border-l")}
								>
									<Tooltip>
										<TooltipTrigger asChild>
											<button
												type="button"
												onClick={() => toggleSort(col.key)}
												className={cn(
													"inline-flex flex-row-reverse items-center gap-1 text-xs font-medium hover:text-foreground",
													active ? "text-foreground" : "text-muted-foreground",
												)}
											>
												{col.label}
												<Icon className="size-3" />
											</button>
										</TooltipTrigger>
										<TooltipContent className="max-w-xs">
											{col.help}
										</TooltipContent>
									</Tooltip>
								</TableHead>
							);
						})}
						{trailingCell && <TableHead className="w-10 bg-muted/50" />}
					</TableRow>
				</TableHeader>

				<TableBody>
					{ordered.map((row) => {
						const isOpen = expandedId === row.id;
						const isExcluded = excludedIds.has(row.id);
						const heading = headingBefore.get(row.id);
						return (
							<Fragment key={row.id}>
								{heading && (
									<TableRow className="hover:bg-transparent">
										<TableCell
											colSpan={columns.length + (trailingCell ? 2 : 1)}
											className="bg-muted/40 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
										>
											{heading}
										</TableCell>
									</TableRow>
								)}
								{/*
								 * Expansion hangs off the name button, not the <tr>: a row
								 * click handler is unreachable by keyboard, and the cells
								 * already contain their own controls.
								 */}
								<TableRow
									className={cn("group/row", isExcluded && "opacity-45")}
								>
									<TableCell className="whitespace-nowrap text-sm">
										<span className="inline-flex items-center gap-2">
											<button
												type="button"
												onClick={() => onToggleExclude(row.id)}
												aria-label={
													isExcluded
														? `Include ${row.name} in totals`
														: `Exclude ${row.name} from totals`
												}
												title={
													isExcluded
														? "Include in totals"
														: "Exclude from totals"
												}
												className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus:opacity-100 group-hover/row:opacity-100 data-[on=true]:opacity-100"
												data-on={isExcluded}
											>
												{isExcluded ? (
													<EyeOff className="size-3.5" />
												) : (
													<Eye className="size-3.5" />
												)}
											</button>
											<button
												type="button"
												onClick={() => onToggleExpand(row.id)}
												aria-expanded={isOpen}
												className={cn(
													"rounded text-left hover:underline",
													isExcluded && "line-through",
												)}
											>
												{row.name}
											</button>
										</span>
										{partition && (
											// biome-ignore lint/a11y/noStaticElementInteractions: mouse-only toggle, chosen deliberately over a keyboard-reachable control.
											<div
												className={cn(
													"mt-1 w-fit",
													partition.onToggleRow && "cursor-pointer",
												)}
												onDoubleClick={() => partition.onToggleRow?.(row.id)}
												title={
													partition.onToggleRow
														? `${partitionLabel(row.id)} — double-click to change`
														: partitionLabel(row.id)
												}
											>
												{partition.of(row.id) ? (
													<Check
														className="size-3.5 text-green-600 dark:text-green-400"
														aria-label={partition.labels.on}
													/>
												) : (
													<X
														className="size-3.5 text-red-600 dark:text-red-400"
														aria-label={partition.labels.off}
													/>
												)}
											</div>
										)}
									</TableCell>
									{columns.map((col, i) => {
										const cell = col.cell(row.id);
										const startsGroup =
											i > 0 && columns[i - 1].group !== col.group;
										const fractionMeasure = col.fractionMeasure ?? measure;
										const fraction =
											cell.absentReason ??
											`${formatAmount(
												cell.numerator,
												fractionMeasure,
											)} / ${formatAmount(cell.denominator, fractionMeasure)}`;

										if (col.renderCell) {
											return (
												<TableCell
													key={col.key}
													className={cn(
														"align-top",
														startsGroup && "border-l",
													)}
												>
													{col.renderCell(row.id)}
												</TableCell>
											);
										}

										return (
											<TableCell
												key={col.key}
												className={cn(
													"text-right align-top",
													startsGroup && "border-l",
												)}
											>
												<div className="flex justify-end">
													<span
														className={cn(
															"inline-block rounded px-1.5 py-0.5 text-sm font-semibold tabular-nums",
															cell.band
																? BAND_CLASS[cell.band]
																: "text-muted-foreground",
														)}
													>
														{formatRate(cell.pct)}
													</span>
												</div>
												<div
													className={cn(
														"text-[10px] tabular-nums text-muted-foreground",
														// Composition columns share one denominator; showing
														// it on every cell is noise, so reveal on hover.
														!col.alwaysShowFraction &&
															"opacity-0 transition-opacity group-hover/row:opacity-100",
													)}
												>
													{fraction}
												</div>
											</TableCell>
										);
									})}
									{trailingCell && (
										<TableCell className="w-10 bg-muted/50 p-0 text-center align-middle">
											{trailingCell(row.id)}
										</TableCell>
									)}
								</TableRow>

								{rowPanel?.(row.id) && (
									<TableRow className="hover:bg-transparent">
										<TableCell
											colSpan={columns.length + (trailingCell ? 2 : 1)}
											className="p-0"
										>
											{rowPanel(row.id)}
										</TableCell>
									</TableRow>
								)}

								{isOpen && (
									<TableRow>
										<TableCell
											colSpan={columns.length + (trailingCell ? 2 : 1)}
											className="bg-muted/30 p-4"
										>
											{renderDetail(row.id)}
										</TableCell>
									</TableRow>
								)}
							</Fragment>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
}
